import './TopBar.sass'

import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useMediaQuery } from '@mui/material'

import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Button from '../Button/Button'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { ContactUsPopup } from './HelpCenter/ContactUsPopup.component'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { LanguageDropdown } from './LanguageDropdown'
import { LanguagePopup } from 'components/common/TopBar/LanguagePopup/LanguagePopup.component'
import Logo from '../Logo/Logo'
import { MUIButton } from '../MUIButton'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import { Path } from 'constants/router'
import { TopBarDropdownClient } from './TopBarDropdown'
import { TopBarDropdownMobile } from './TopBarDropdownMobile'
import { TopBarNavigation } from './TopBarNavigation'
import { WorkspaceDropdown } from './WorkspaceDropdown'
import { NewOrderButton } from 'components/pages/PurchaseFlowRevamp/entry/NewOrderButton.component'
import { PropertySelectionDrawer } from 'components/pages/PurchaseFlowRevamp/entry/PropertySelectionDrawer.component'
import { usePfEntryNavigation } from 'components/pages/PurchaseFlowRevamp/entry/usePfEntryNavigation'
import { usePfEntryPrimaryAction } from 'components/pages/PurchaseFlowRevamp/entry/usePfEntryPrimaryAction'
import { clearBeckaSession } from 'utils/becka/becka'
import { discardOrderAttempt, startOrderAttempt } from 'utils/analytics/orderAttempt'
import { useAuth0 } from 'utils/auth'
import { useCanUserOrder } from 'utils/user/canUserOrder'
import { useIsRegistrationFlow } from 'utils/hooks'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @interface Props for TopBar component
 */
export interface Props {
  /** The additional classes to append */
  className?: string
}

/**
 * @component A component usually display at the top of the page containing a logo, login/sign-up buttons and profile info as well as menu links for logged in users. Contains also mobile version of a menu.
 * @example <TopBar className="class" />
 */
const TopBar: React.FC<Props> = ({
  className = '',
}) => {
  const { t } = useTranslation(['topbar', 'authentication', 'gallery', 'category', 'product', 'dashboard_client', 'workspace_profile'])
  const { isAuthenticated, loading, logout, user, roles } = useAuth0()
  const isRegistrationFlow = useIsRegistrationFlow()
  const {
    currentUserWorkspace,
    clientData,
  } = useUserData()

  const isMobile = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  const canUserOrder = useCanUserOrder()
  const location = useLocation()
  const navigate = useNavigate()
  const [isLanguageOpen, setIsLanguageOpen] = useState(false)
  const [isContactUsOpen, setIsContactUsOpen] = useState(false)

  const { isEnabled: allowPfRevamp } = usePfEntryNavigation()
  const [isPropertyDrawerOpen, setIsPropertyDrawerOpen] = useState(false)

  /** logout action handle */
  const handleLogout = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault()

    clearBeckaSession()

    logout()
    closeMenuHandler(e)
  }

  // Open / Close logic
  /** inner menu element reference */
  const menuRef = useRef<HTMLDivElement>(null)
  const [showMenu, setShowMenu] = useState(false)

  /** action called upon mobile menu closing */
  const closeMenuAction = useCallback((e: any) => {
    if (menuRef.current?.contains(e.target)) return

    setShowMenu(false)
    document.removeEventListener('click', closeMenuAction)
  }, [])

  /** action called upon mobile menu opening */
  const openMenuAction = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault()
    if (showMenu) return

    setShowMenu(true)
    window.setTimeout(() => {
      document.addEventListener('click', closeMenuAction)
    }, 0)
  }, [showMenu, closeMenuAction])

  /** timeline for close animations */
  const closeMenuHandler = useCallback((e: React.MouseEvent<Element, MouseEvent>) => {
    document.removeEventListener('click', closeMenuAction)
    setShowMenu(false)
  }, [closeMenuAction])

  // Unsubscribe event listener
  useEffect(() => {
    return () => {
      document.removeEventListener('click', closeMenuAction)
    }
  }, [closeMenuAction])

  // Close mobile menu when route changes
  useEffect(() => {
    document.removeEventListener('click', closeMenuAction)
    setShowMenu(false)
  }, [location.pathname, closeMenuAction])

  /** handle menu when window resize */
  useEffect(() => {
    const resizeHandler = (e: any) => {
      const screenWidth = window.innerWidth
      if (screenWidth > 800) {
        closeMenuHandler(e)
      }
    }
    window.addEventListener('resize', resizeHandler)
    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [closeMenuAction, closeMenuHandler])

  if (!isAuthenticated && (location.pathname === '' || location.pathname === '/')) className += ' index-notlogged'

  const canShowOrderButton = (roles.isClient || roles.isAdmin) && canUserOrder && (!roles.isClient || !!currentUserWorkspace)

  const handleLogAnalyticsEvent = useCallback((eventName: string, options?: {}) => {
    logAnalyticsEvent(eventName, {
      userEmail: clientData?.email,
      organizationId: clientData?.organizationId,
      ...options
    })
  }, [clientData?.email, clientData?.organizationId])

  const handleOpenPropertyDrawer = useCallback(() => {
    setIsPropertyDrawerOpen(true)
  }, [])

  const handleOpenOrderDrawer = useCallback(() => {
    handleLogAnalyticsEvent(AnalyticsEvent.CLICK_ORDER_NEW_VISUALS, { orderAttemptId: startOrderAttempt('top_bar'), launched_from: 'top_bar' })
    setIsPropertyDrawerOpen(true)
  }, [handleLogAnalyticsEvent])

  // Legacy (revamp OFF) order CTA fires the same analytics as the desktop button.
  const handleLegacyOrderNavigate = useCallback(() => {
    handleLogAnalyticsEvent(AnalyticsEvent.CLICK_ORDER_NEW_VISUALS, { orderAttemptId: startOrderAttempt('top_bar'), launched_from: 'top_bar' })
  }, [handleLogAnalyticsEvent])

  // Mobile order CTA reuses the desktop split-button's primary-action decision (DRY).
  const { handlePrimaryAction: handleMobileOrderClick } = usePfEntryPrimaryAction({
    onOpenPropertyDrawer: handleOpenOrderDrawer,
    onLegacyNavigate: handleLegacyOrderNavigate,
  })

  // The mobile menu logs its own event and unmounts on the same click, so the drawer it opens is held here.
  const { handlePrimaryAction: handleMenuOrderAction } = usePfEntryPrimaryAction({
    onOpenPropertyDrawer: handleOpenPropertyDrawer,
  })

  return (
    <Fragment>

      <header className={`topbar ${className} ${!(isAuthenticated && user) && 'login-topbar'}`.replace(/\s+/igm, ' ').trim()}>

        <Link
          to={Path.INDEX}
          className="logo-link"
          onClick={() => handleLogAnalyticsEvent(AnalyticsEvent.CLICK_BACKBONE_LOGO)}
        >
          <Logo />
        </Link>

        {!isMobile && roles.isClient && !isRegistrationFlow &&
          <WorkspaceDropdown handleLogAnalyticsEvent={handleLogAnalyticsEvent} />
        }

        {/** Navigation buttons */}
        {isAuthenticated && !isMobile && <TopBarNavigation />}

        <div className="push-right onlymobile">
          {!(isAuthenticated && user) ?
            <LanguageDropdown />
            :
            <Fragment>

              {canShowOrderButton &&
                <MUIButton
                  size="sm"
                  className="mobile-order-button"
                  startIcon={<AddRoundedIcon sx={{ fontSize: '1.8rem' }} />}
                  onClick={handleMobileOrderClick}
                >
                  {t(roles.isClient ? 'client_order_button' : 'admin_order_button')}
                </MUIButton>
              }

              <Button
                className="burger-menu-button"
                type="secondary"
                onClick={(e) => {
                  handleLogAnalyticsEvent(AnalyticsEvent.CLICK_MAIN_MENU)
                  showMenu ? closeMenuHandler(e) : openMenuAction(e)
                }}
              >
                {user && <img src={user.picture} alt={t('profile')} />}
                {showMenu ? <CloseRoundedIcon /> : <MenuRoundedIcon />}
              </Button>

            </Fragment>
          }
        </div>

        {!loading &&
          <Fragment>
            {isAuthenticated && user ?
              <div className="profile-block push-right">

                {canShowOrderButton && !allowPfRevamp &&
                  <MUIButton
                    className='order-visuals-button'
                    onClick={() => {
                      handleLogAnalyticsEvent(AnalyticsEvent.CLICK_ORDER_NEW_VISUALS, { orderAttemptId: startOrderAttempt('top_bar'), launched_from: 'top_bar' })
                      navigate(Path.PURCHASE_FLOW)
                    }}
                  >
                    {t(roles.isClient ? 'client_order_button' : 'admin_order_button')}
                  </MUIButton>
                }

                {canShowOrderButton && allowPfRevamp &&
                  <NewOrderButton
                    primaryLabel={t(roles.isClient ? 'client_order_button' : 'admin_order_button')}
                    onOpenPropertyDrawer={handleOpenOrderDrawer}
                  />
                }

                {<LanguagePopup isLanguageOpen={isLanguageOpen} closeCallback={() => setIsLanguageOpen(!isLanguageOpen)} />}

                {<ContactUsPopup isContactUsOpen={isContactUsOpen} setIsContactUsOpen={setIsContactUsOpen} />}

                <TopBarDropdownClient
                  setIsLanguageOpen={() => setIsLanguageOpen(!isLanguageOpen)}
                  setIsContactUsOpen={() => setIsContactUsOpen(!isContactUsOpen)}
                  handleLogout={handleLogout}
                  closeMenuHandler={closeMenuHandler}
                  handleLogAnalyticsEvent={handleLogAnalyticsEvent}
                />

              </div>
              :
              <div className="login-buttons push-right">
                <LanguageDropdown />
              </div>
            }
          </Fragment>
        }
      </header>

      <TopBarDropdownMobile
        showMenu={showMenu}
        setIsLanguageOpen={() => setIsLanguageOpen(!isLanguageOpen)}
        setIsContactUsOpen={() => setIsContactUsOpen(!isContactUsOpen)}
        handleLogout={handleLogout}
        closeMenuHandler={closeMenuHandler}
        handleLogAnalyticsEvent={handleLogAnalyticsEvent}
        onOpenPropertyDrawer={handleOpenPropertyDrawer}
        onStartNewOrder={handleMenuOrderAction}
      />

      {allowPfRevamp &&
        <PropertySelectionDrawer
          isOpen={isPropertyDrawerOpen}
          onClose={() => setIsPropertyDrawerOpen(false)}
          onDismiss={discardOrderAttempt}
          showDraftOrders
        />
      }

    </Fragment>
  )
}

export default TopBar
